import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { Patient } from '@chiroup/core/types/Patient.type';
import { HealthcomInsurance } from '@chiroup/core/types/PatientInsurance.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';
import { kioskSession } from './kioskSession';
import { requestSession } from './requestSession';

// Public PatientRequest service. The auth bootstrap (`/requests/auth`)
// is unauthenticated and calls the schedule backend's `api-noauth`
// API. Once the validationCode is verified the backend issues a
// request-scoped session token that we stash in `requestSession`;
// every subsequent call uses `api` (whose Bearer header comes from
// `main.tsx`, which falls back to `requestSession` when no patient
// session is present).
//
// `validationCode` parameters are kept on every method signature so
// existing callers don't have to change at the same time, but they
// are no longer sent over the wire — the schedule backend's wrapper
// trusts the Bearer session instead.

const API = 'api';
const API_NOAUTH = 'api-noauth';

// Per-call Authorization header. Same defense as in `kiosk.service`:
// `main.tsx`'s fallback chain prefers `portalSession` when it's set,
// which would route a request-flow call to a patient-scoped token if
// the device also had a portal login. Per-call headers override the
// global callback, so we always send the intended request/kiosk token here.
const requestAuthHeaders = (opts?: { kiosk?: boolean }): { Authorization: string } => {
  const token = opts?.kiosk ? kioskSession.getToken() : requestSession.getToken();

  return { Authorization: token ? `Bearer ${token}` : '' };
};

export type RequestBootstrap = {
  steps: RequestStep[];
  language: 'en' | 'es';
  gender: Genders;
  age: number;
  id: string;
  ccsFollowups?: string[];
  guardian?: boolean;
  validationCode?: string | number;
  encounterId?: string;
  prMap?: { [key: string]: string };
};

export type ClinicInfo = {
  logo?: string;
  name?: string;
  primaryColor?: string;
  accentColor?: string;
  doNotAllowUnrelatedRegions?: boolean;
};

export type AuthRequestResponse = {
  message?: string;
  data?: {
    phone?: string;
    email?: string;
    language?: 'en' | 'es';
    validationCode?: string | number;
    clinicInfo?: ClinicInfo;
    // Server returns the full request payload alongside validationCode
    // once the user has authed; we treat the rest of the body as the
    // bootstrap shape.
  } & Partial<RequestBootstrap>;
};

const s3PostWithRetry = async (url: string, form: FormData): Promise<Response> => {
  try {
    return await fetch(url, { method: 'POST', body: form });
  } catch (_err) {
    // Single retry after 1s — handles Safari's transient "Load failed"
    // when resuming from camera on iOS.
    await new Promise(r => setTimeout(r, 1000));

    return await fetch(url, { method: 'POST', body: form });
  }
};

const requestService = () => {
  const apiUrl = import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string;

  // Auth — GET asks the server for available delivery methods or sends a
  // code. Code verification uses POST so the validationCode never lands
  // in browser history, referrers, or API Gateway query logs.
  const authRequest = async (params: {
    requestId: string;
    validationCode?: string | number;
    method?: string;
  }): Promise<AuthRequestResponse> => {
    const queryParams: { [key: string]: string } = { requestId: params.requestId };

    if (params.validationCode != null && params.requestId) {
      const res: AuthRequestResponse = await ChiroUpAPI.post(API_NOAUTH, '/requests/auth', {
        body: {
          requestId: params.requestId,
          validationCode: String(params.validationCode),
        },
      });
      const data = res?.data as any;

      if (data?.token) {
        requestSession.setToken(data.token, params.requestId, data.tokenExpiresAt);
      }

      return res;
    }

    if (params.method && params.requestId) {
      queryParams.method = params.method;
    }

    const res: AuthRequestResponse = await ChiroUpAPI.get(API_NOAUTH, '/requests/auth', {
      queryParams,
    });

    const data = res?.data as any;

    if (data?.token) {
      requestSession.setToken(data.token, params.requestId, data.tokenExpiresAt);
    }

    return res;
  };

  // Survey question answer submit — same endpoint the legacy app
  // already uses. Drives next-question navigation in the runner.
  const submitSurveyAnswer = async ({
    requestId,
    patientSurveyId,
    patientSurveyQuestionId,
    answer,
    validationCode,
    prMap,
    encounterId,
    kiosk,
    remainingSteps,
  }: {
    requestId: string;
    patientSurveyId: string;
    patientSurveyQuestionId: string;
    answer: SurveyQuestionAnswer;
    validationCode: string | number;
    prMap?: { [key: string]: string };
    encounterId?: string;
    kiosk?: boolean;
    remainingSteps?: number[];
  }): Promise<{ question?: SurveyQuestion; progress?: SurveyProgress }> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.put(
      API,
      `/requests/${pr}/survey/${patientSurveyId}/${patientSurveyQuestionId}`,
      {
        headers: requestAuthHeaders({ kiosk }),
        body: {
          patientSurveyQuestionAnswer: answer,
          validationCode,
          remainingSteps: remainingSteps ?? [],
          prMap: prMap ?? {},
          kiosk: !!kiosk,
          encounterId,
        },
      }
    );
  };

  // Previous-question lookup for the back button.
  const getPreviousQuestion = async ({
    requestId,
    patientSurveyId,
    questionId,
    prMap,
    kiosk,
  }: {
    requestId: string;
    patientSurveyId: string;
    questionId: string;
    prMap?: { [key: string]: string };
    kiosk?: boolean;
  }): Promise<{
    question: SurveyQuestion;
    previousQuestion: string;
    progress: SurveyProgress;
  }> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.get(API, `/requests/${pr}/survey/${patientSurveyId}/previous/${questionId}`, {
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  // Consent — validationCode-authed PUT that stitches the signed
  // acknowledgment page onto the original consent PDF. `preview: true`
  // returns a downloadable URL without marking the consent complete.
  const submitConsent = async ({
    requestId,
    patientConsentId,
    consentUrl,
    signature,
    validationCode,
    prMap,
    remainingSteps,
    preview,
    kiosk,
  }: {
    requestId: string;
    patientConsentId: string;
    consentUrl: string;
    signature: string | undefined;
    validationCode: string | number;
    prMap?: { [key: string]: string };
    remainingSteps?: number[];
    preview?: boolean;
    kiosk?: boolean;
  }): Promise<{ url?: string }> => {
    const pr = prMap?.[patientConsentId] || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/consent/${patientConsentId}`, {
      headers: requestAuthHeaders({ kiosk }),
      body: {
        consent: consentUrl,
        signature,
        preview: !!preview,
        validationCode,
        remainingSteps: remainingSteps ?? [],
      },
    });
  };

  // Photo ID / insurance card uploads — pre-sign + POST to S3 directly.
  const uploadFile = async ({
    type,
    requestId,
    validationCode,
    file,
    side,
    prMap,
    surveyType,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
    validationCode: string | number;
    file: File;
    side: 'front' | 'back';
    prMap?: { [key: string]: string };
    surveyType?: string;
  }): Promise<string> => {
    const fileType = file.type.split('/')[1];
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';
    const pr =
      type === 'photoId'
        ? prMap?.uploadPhotoId || requestId
        : prMap?.uploadInsuranceCard || requestId;
    const surveyTypeQuery =
      type === 'insuranceCard' ? `&surveyType=${surveyType ?? 'insuranceCard'}` : '';

    const token = requestSession.getToken();
    const resp = await fetch(
      `${apiUrl}/requests/${pr}/${subPath}?fileType=${fileType}&side=${side}${surveyTypeQuery}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );

    if (!resp.ok) {
      throw new Error(`Failed to start upload (${resp.status})`);
    }

    const res: { fields: { [key: string]: string }; url: string } = await resp.json();
    const form = new FormData();

    Object.entries(res.fields).forEach(([key, val]) => form.append(key, val));
    form.append('file', file);

    const s3Resp = await s3PostWithRetry(res.url, form);

    if (!s3Resp.ok) {
      throw new Error(`Upload to S3 failed (${s3Resp.status})`);
    }

    return res.fields.key;
  };

  const skipUpload = async ({
    type,
    requestId,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
    // Legacy parameter — kept on call signatures while existing
    // callers transition. The schedule backend authenticates from
    // the bearer token, so we no longer send it on the wire.
    validationCode?: string | number;
  }): Promise<void> => {
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';

    await ChiroUpAPI.get(API, `/requests/${requestId}/${subPath}`, {
      queryParams: { skip: 'true' },
      headers: requestAuthHeaders(),
    });
  };

  // Insurance + basic information forms — kept for parity with the
  // legacy stepper. Implementations land in later passes when the form
  // panels are built.
  const insuranceCreate = async (
    requestId: string,
    _validationCode: string | number,
    insurances: Partial<HealthcomInsurance>[],
    kiosk?: boolean
  ): Promise<Response> => {
    return ChiroUpAPI.put(API, `/requests/${requestId}/insurance`, {
      body: insurances,
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  const skipInsuranceCreate = async (
    requestId: string,
    _validationCode?: string | number,
    kiosk?: boolean
  ) => {
    return ChiroUpAPI.put(API, `/requests/${requestId}/insurance`, {
      queryParams: { skip: 'true' },
      headers: requestAuthHeaders({ kiosk }),
      body: {},
    });
  };

  const submitInformation = async (
    requestId: string,
    data: Partial<Patient>,
    _validationCode: string | number,
    prMap?: { [key: string]: string },
    kiosk?: boolean
  ): Promise<Response> => {
    const pr = prMap?.basicInformation || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/information`, {
      body: data,
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  // Whole-survey submit — used by the CCS / reevaluation flow which
  // posts every question + body-picker answer in one shot rather
  // than per-question. Routes to the legacy healthcom-backend (which
  // still owns the heavy CCS-specific orchestration: PatientTracks,
  // complaints, visit creation). Auth is the legacy validationCode
  // in the body, not the new bearer session.
  const submitSurvey = async (
    requestId: string,
    patientSurveyId: string,
    questions: SurveyQuestion[],
    kiosk: boolean,
    prMap?: { [key: string]: string },
    spanishQuestions?: SurveyQuestion[],
    ccsFollowups?: string[],
    validationCode?: string | number,
    encounterId?: string
  ): Promise<any> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.put('healthcom-api-noauth', `/requests/${pr}/survey/${patientSurveyId}`, {
      body: {
        questions,
        spanishQuestions,
        ccsFollowups,
        validationCode,
        prMap,
        kiosk,
        encounterId,
      },
    });
  };

  return {
    authRequest,
    submitSurveyAnswer,
    submitSurvey,
    getPreviousQuestion,
    submitConsent,
    uploadFile,
    skipUpload,
    insuranceCreate,
    skipInsuranceCreate,
    submitInformation,
  };
};

export default requestService();
